/**
 * GameState factory, turn state machine, win condition.
 * Pure functions — no side effects, no rendering.
 */

import type {
  GameState,
  GameConfig,
  GamePhase,
  PlayerState,
  PlanetState,
  ProjectileState,
  StructureState,
  HazardState,
  RuinState,
  FloraState,
  RockState,
  EnvironmentalEffectState,
  PlantedFlagState,
  StructureType,
  TurnInput,
  FireParams,
  Vec3,
  WeaponKey,
  PlanetSizePreset,
  MapLayout,
  StarType,
  SystemPreset,
  WinResult,
  ModeConfig,
  CarryableObjectState,
  CollectibleNode,
  DrillSiteState,
  GroundItemState,
} from '../types';
import { DEFAULT_STARTING_CBP, WEAPONS, PLANT_FLAG_MOVEMENT_COST, MEGAPLANET_THRUST_RADIUS } from '../constants';
import { structureHealth, bodyHasSeat, initialSeats } from './body';
import { withBuilders } from './buildingScale';
import { megaPlanetPowerMult } from './physics';
import { getSurfaceRadius } from './surface';
import { canPlantOnPlanet } from './stakes';
import { vecAdd, vecScale, vecNormalize, vecLength, surfaceTangentFrame } from '../math';
import { calculateIncome } from './economy';
import { isModeGameOver, checkModeWinCondition, initModeState } from './modes';
import { getCarrierMovementPenalty } from './carryables';
import { clampFirePower } from './stabilizers';
import { dacos, dcos, dsin } from './dmath';

// =============================================================================
// STATE FACTORY
// =============================================================================

export function createGameConfig(
  numPlayers: number,
  numBots: number,
  numPlanets: number,
  seed: string,
  numStars: number = 0,
  numWormholePairs: number = 0,
  numTeams?: number,
  humanTeams?: number,
  botTeams?: number,
  unitsPerTeam?: number,
  botUnitsPerTeam?: number,
  turnOrderMode: 'sequential' | 'alternating' = 'sequential',
  friendlyFire: number = 0,
  datalinkedLOS: boolean = false,
  botFogEnabled: boolean = true,
  adaptiveBotPersonality: boolean = false,
  hideEnemyDeploy: boolean = false,
  hazardDensity: 'none' | 'some' | 'many' = 'some',
  ruinDensity: 'none' | 'few' | 'some' | 'many' = 'some',
  floraDensity: 'none' | 'some' | 'many' = 'some',
  planetSize: PlanetSizePreset = 'mixed',
  mapLayout: MapLayout = 'random',
  starType: StarType | 'random' = 'random',
  systemPreset: SystemPreset = 'custom',
  modeConfig: ModeConfig = { mode: 'deathmatch' },
  allowedWeapons?: WeaponKey[],
  visibility: 'fog' | 'free' = 'fog',
  fallDamageMultiplier: number = 1.0,
  startingCBP: number = DEFAULT_STARTING_CBP,
  previewQuality: number = 1,
  stabilizers: boolean = false,
): GameConfig {
  const teams = numTeams ?? numPlayers;
  const hTeams = humanTeams ?? (numPlayers - numBots);
  const bTeams = botTeams ?? numBots;
  const upt = unitsPerTeam ?? 1;
  const bupt = botUnitsPerTeam ?? 1;
  return {
    numPlayers,
    numBots,
    numPlanets,
    numStars,
    numWormholePairs,
    seed,
    startingCBP,
    turnTimeLimit: null,
    numTeams: teams,
    humanTeams: hTeams,
    botTeams: bTeams,
    unitsPerTeam: upt,
    botUnitsPerTeam: bupt,
    turnOrderMode,
    turnOrder: [],  // computed later via computeTurnOrder
    friendlyFire,
    datalinkedLOS,
    botFogEnabled,
    adaptiveBotPersonality,
    hideEnemyDeploy,
    hazardDensity,
    ruinDensity,
    floraDensity,
    planetSize,
    mapLayout,
    starType,
    systemPreset,
    modeConfig,
    allowedWeapons,
    visibility,
    fallDamageMultiplier,
    previewQuality,
    stabilizers,
  };
}

export function createInitialGameState(config: GameConfig): GameState {
  return {
    phase: 'menu',
    config,
    turnNumber: 1,
    currentPlayerIndex: 0,
    planets: [],
    players: [],
    structures: [],
    hazards: [],
    ruins: [],
    flora: [],
    rocks: [],
    environmentalEffects: [],
    plantedFlags: [],
    carryables: [],
    groundItems: [],
    crashedDropCans: [],
    collectibles: [],
    projectile: null,
    pendingClusters: [],
    fog: [],
    botCommands: [],
    modeState: initModeState(config.modeConfig),
  };
}

// =============================================================================
// TURN ORDER
// =============================================================================

/**
 * Compute deterministic turn order from player array and mode.
 * 'sequential': All team 0 acts, then team 1, etc. → [A0, A1, B0, B1]
 * 'alternating': Interleave across teams → [A0, B0, A1, B1]
 * Returns number[] mapping turn position → player index.
 */
export function computeTurnOrder(
  players: PlayerState[],
  mode: 'sequential' | 'alternating'
): number[] {
  // Group alive players by teamIndex, preserving order within each team
  const teamMap = new Map<number, number[]>();
  for (let i = 0; i < players.length; i++) {
    if (!players[i].alive) continue;  // Dead players excluded from turn order
    const team = players[i].teamIndex;
    if (!teamMap.has(team)) teamMap.set(team, []);
    teamMap.get(team)!.push(i);
  }

  // Sort teams by their first player's index for determinism
  const teamKeys = [...teamMap.keys()].sort((a, b) => {
    return teamMap.get(a)![0] - teamMap.get(b)![0];
  });

  const order: number[] = [];

  if (mode === 'sequential') {
    // All of team A, then all of team B, etc.
    for (const team of teamKeys) {
      order.push(...teamMap.get(team)!);
    }
  } else {
    // Alternating: interleave across teams
    const maxUnits = Math.max(...teamKeys.map(t => teamMap.get(t)!.length));
    for (let unitIdx = 0; unitIdx < maxUnits; unitIdx++) {
      for (const team of teamKeys) {
        const members = teamMap.get(team)!;
        if (unitIdx < members.length) {
          order.push(members[unitIdx]);
        }
      }
    }
  }

  return order;
}

// =============================================================================
// PHASE TRANSITIONS
// =============================================================================

export function setPhase(state: GameState, phase: GamePhase): GameState {
  return { ...state, phase };
}

export function startAimingPhase(state: GameState): GameState {
  // Skip dead players
  let index = state.currentPlayerIndex;
  let safety = 0;
  while (!state.players[index].alive && safety < state.players.length) {
    index = (index + 1) % state.players.length;
    safety++;
  }

  return {
    ...state,
    phase: 'aiming',
    currentPlayerIndex: index,
  };
}

export function startFiringPhase(
  state: GameState,
  projectile: ProjectileState
): GameState {
  return {
    ...state,
    phase: 'firing',
    projectile,
  };
}

export function startResolvingPhase(state: GameState): GameState {
  return {
    ...state,
    phase: 'resolving',
    projectile: null,
  };
}

// =============================================================================
// TURN ADVANCEMENT
// =============================================================================

export function advanceTurn(state: GameState): GameState {
  // Mode-aware game over check (elimination + turn limit + future objectives)
  if (isModeGameOver(state)) {
    return { ...state, phase: 'gameover' };
  }

  // Recompute turn order from alive players to maintain team alternation
  const turnOrder = computeTurnOrder(state.players, state.config.turnOrderMode);
  const useTurnOrder = turnOrder.length > 0;

  let nextIndex: number;
  let newTurnNumber = state.turnNumber;

  if (useTurnOrder) {
    const currentPos = turnOrder.indexOf(state.currentPlayerIndex);
    let nextPos: number;
    if (currentPos === -1) {
      // Current player died (e.g. environmental effect) and isn't in alive order
      nextPos = 0;
    } else {
      nextPos = (currentPos + 1) % turnOrder.length;
    }
    nextIndex = turnOrder[nextPos];
    // Increment turn number on wrap-around
    if (currentPos === -1 || nextPos <= currentPos) newTurnNumber++;
  } else {
    // Legacy fallback: simple index increment
    nextIndex = (state.currentPlayerIndex + 1) % state.players.length;
    let safety = 0;
    while (!state.players[nextIndex].alive && safety < state.players.length) {
      nextIndex = (nextIndex + 1) % state.players.length;
      safety++;
    }
    if (nextIndex <= state.currentPlayerIndex) newTurnNumber++;
  }

  // Check turn limit — end game if new turn exceeds it
  const turnLimit = state.config.modeConfig?.turnLimit;
  if (turnLimit && newTurnNumber > turnLimit) {
    return { ...state, turnNumber: newTurnNumber, phase: 'gameover' };
  }

  // Reset movement and add income for the next player
  const income = calculateIncome(state.players[nextIndex].id, state.structures);
  const newPlayers = state.players.map((p, i) =>
    i === nextIndex ? { ...p, movementRemaining: p.movementRange, cbp: p.cbp + income } : p
  );

  return {
    ...state,
    players: newPlayers,
    currentPlayerIndex: nextIndex,
    turnNumber: newTurnNumber,
    phase: 'aiming',
  };
}

// =============================================================================
// WIN CONDITION
// =============================================================================

export function checkWinCondition(state: GameState): WinResult | null {
  return checkModeWinCondition(state);
}

export function isGameOver(state: GameState): boolean {
  return isModeGameOver(state);
}

// =============================================================================
// PROJECTILE CREATION
// =============================================================================

export function createProjectile(
  player: PlayerState,
  planets: PlanetState[],
  params: FireParams,
  config?: Pick<GameConfig, 'stabilizers'>,
): ProjectileState {
  const planet = planets[player.planetIndex];
  const surfaceNormal = vecNormalize(player.surfaceDir);

  // Calculate world position on planet surface
  const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
  const worldPos = vecAdd(planet.position, vecScale(surfaceNormal, surfaceRadius));

  // Offset slightly above surface
  const startPos = vecAdd(worldPos, vecScale(surfaceNormal, 1.2));

  // Calculate launch direction from azimuth/elevation
  const launchDir = computeLaunchDirection(surfaceNormal, params.azimuth, params.elevation);
  const weapon = WEAPONS[params.weaponKey];
  // Stabilizers: a unit that moved this turn cannot deliver full power (no-op with the setting off);
  // a weapon's own cap (§12.7 field class) sits under that
  const power = Math.min(clampFirePower(params.power, player, config), weapon.maxPower ?? Infinity);
  const speed = power * megaPlanetPowerMult(planet.radius);
  const vel = vecScale(launchDir, speed);

  return {
    pos: startPos,
    vel,
    thrustDir: launchDir,
    thrustMag: speed,
    fuelRemaining: weapon.fuelDuration * (player.modifiers?.fuelMult ?? 1),
    weaponKey: params.weaponKey,
    phase: 'burn',
    time: 0,
    ownerId: player.id,
    lastWormholeId: null,
    wormholeTransits: 0,
  };
}

export function computeLaunchDirection(
  normal: Vec3,
  azimuth: number,
  elevation: number
): Vec3 {
  // Shared tangent frame (math.ts) — same frame as model facing and bot aim.
  // Normalize defensively; mirrors compute_launch_direction in lib.rs.
  normal = vecNormalize(normal);
  const { tx, ty } = surfaceTangentFrame(normal);

  const ce = dcos(elevation);
  const se = dsin(elevation);
  const ca = dcos(azimuth);
  const sa = dsin(azimuth);

  // Direction in local coordinates, then transform to world
  return vecNormalize({
    x: tx.x * ce * ca + ty.x * ce * sa + normal.x * se,
    y: tx.y * ce * ca + ty.y * ce * sa + normal.y * se,
    z: tx.z * ce * ca + ty.z * ce * sa + normal.z * se,
  });
}

// =============================================================================
// PLAYER HELPERS
// =============================================================================

export function getPlayerWorldPosition(
  player: PlayerState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[player.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
  const normal = vecNormalize(player.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getPlayerSurfaceNormal(player: PlayerState): Vec3 {
  return vecNormalize(player.surfaceDir);
}

// =============================================================================
// CLUSTER MANAGEMENT
// =============================================================================

export function addPendingCluster(
  state: GameState,
  pos: Vec3,
  vel: Vec3,
  weaponKey: WeaponKey
): GameState {
  return {
    ...state,
    pendingClusters: [...state.pendingClusters, { pos, vel, weaponKey }],
  };
}

export function popPendingCluster(
  state: GameState
): { state: GameState; cluster: { pos: Vec3; vel: Vec3; weaponKey: WeaponKey } | null } {
  if (state.pendingClusters.length === 0) {
    return { state, cluster: null };
  }
  const [cluster, ...rest] = state.pendingClusters;
  return {
    state: { ...state, pendingClusters: rest },
    cluster,
  };
}

// =============================================================================
// MOVEMENT
// =============================================================================

/**
 * Calculate arc distance between two directions on a sphere.
 */
export function arcDistance(dir1: Vec3, dir2: Vec3, radius: number): number {
  const d1 = vecNormalize(dir1);
  const d2 = vecNormalize(dir2);
  const dot = d1.x * d2.x + d1.y * d2.y + d1.z * d2.z;
  const angle = dacos(Math.max(-1, Math.min(1, dot)));
  return angle * radius;
}

/**
 * Check if a player can move to a target direction on their current planet.
 */
export function canMoveTo(
  player: PlayerState,
  targetDir: Vec3,
  planets: PlanetState[],
  carryables?: import('../types').CarryableObjectState[],
  modeConfig?: ModeConfig
): { canMove: boolean; cost: number } {
  const planet = planets[player.planetIndex];
  const distance = arcDistance(player.surfaceDir, targetDir, planet.radius);

  // Apply carrier speed penalty (carrying makes movement cost more)
  let effectiveCost = distance;
  if (carryables && modeConfig) {
    const penalty = getCarrierMovementPenalty(player.id, carryables, modeConfig);
    if (penalty > 0) {
      effectiveCost = distance / (1 - penalty);
    }
  }

  const canMove = effectiveCost <= player.movementRemaining;
  return { canMove, cost: effectiveCost };
}

/**
 * Move a player to a new surface direction on their current planet.
 * Returns updated game state with player in new position and movement deducted.
 */
export function movePlayer(
  state: GameState,
  playerId: number,
  targetDir: Vec3
): GameState {
  const playerIndex = state.players.findIndex(p => p.id === playerId);
  if (playerIndex < 0) return state;

  const player = state.players[playerIndex];
  if (player.tetheredTurns && player.tetheredTurns > 0) return state; // Tethered: can't move

  const planet = state.planets[player.planetIndex];
  const distance = arcDistance(player.surfaceDir, targetDir, planet.radius);

  // Apply carrier speed penalty
  let effectiveCost = distance;
  const penalty = getCarrierMovementPenalty(player.id, state.carryables, state.config.modeConfig);
  if (penalty > 0) {
    effectiveCost = distance / (1 - penalty);
  }

  if (effectiveCost > player.movementRemaining) {
    return state; // Can't move that far
  }

  const normalizedDir = vecNormalize(targetDir);
  const newPlayers = state.players.map((p, i) =>
    i === playerIndex
      ? {
          ...p,
          surfaceDir: normalizedDir,
          movementRemaining: p.movementRemaining - effectiveCost,
        }
      : p
  );

  // Update carried object position to follow player
  const newCarryables = state.carryables.map(c =>
    c.carriedBy === playerId
      ? { ...c, planetIndex: player.planetIndex, surfaceDir: { ...normalizedDir } }
      : c
  );

  return { ...state, players: newPlayers, carryables: newCarryables };
}

/**
 * Get all reachable directions from player's current position.
 * Returns points on the planet surface within movement range.
 */
export function getReachableArea(
  player: PlayerState,
  planets: PlanetState[],
  resolution: number = 24
): Vec3[] {
  const planet = planets[player.planetIndex];
  const maxAngle = player.movementRemaining / planet.radius;
  const currentDir = vecNormalize(player.surfaceDir);

  // Create orthonormal basis
  let ref: Vec3 = { x: 1, y: 0, z: 0 };
  const dot = currentDir.x * ref.x + currentDir.y * ref.y + currentDir.z * ref.z;
  if (Math.abs(dot) > 0.9) {
    ref = { x: 0, y: 1, z: 0 };
  }

  // tangent = currentDir × ref
  const tx = currentDir.y * ref.z - currentDir.z * ref.y;
  const ty = currentDir.z * ref.x - currentDir.x * ref.z;
  const tz = currentDir.x * ref.y - currentDir.y * ref.x;
  const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz);
  const tangent = { x: tx / tLen, y: ty / tLen, z: tz / tLen };

  // bitangent = currentDir × tangent
  const bx = currentDir.y * tangent.z - currentDir.z * tangent.y;
  const by = currentDir.z * tangent.x - currentDir.x * tangent.z;
  const bz = currentDir.x * tangent.y - currentDir.y * tangent.x;
  const bitangent = { x: bx, y: by, z: bz };

  const points: Vec3[] = [];

  // Generate circle of reachable points at max range
  for (let i = 0; i < resolution; i++) {
    const azimuth = (i / resolution) * Math.PI * 2;
    const ca = dcos(azimuth);
    const sa = dsin(azimuth);
    const ce = dcos(maxAngle);
    const se = dsin(maxAngle);

    // Rotate from currentDir by maxAngle in direction of azimuth
    const x = currentDir.x * ce + (tangent.x * ca + bitangent.x * sa) * se;
    const y = currentDir.y * ce + (tangent.y * ca + bitangent.y * sa) * se;
    const z = currentDir.z * ce + (tangent.z * ca + bitangent.z * sa) * se;

    points.push(vecNormalize({ x, y, z }));
  }

  return points;
}

// =============================================================================
// STRUCTURE PLACEMENT
// =============================================================================

export function addStructure(
  state: GameState,
  ownerId: number,
  structureType: StructureType,
  planetIndex: number,
  surfaceDir: Vec3
): GameState {
  const id = state.structures.length;
  const structure: StructureState = {
    id,
    type: structureType,
    planetIndex,
    surfaceDir: vecNormalize(surfaceDir),
    health: structureHealth(structureType),
    ownerId,
    ...(bodyHasSeat(structureType) ? { seats: initialSeats(structureType) } : {}),
  };
  return withBuilders({ ...state, structures: [...state.structures, structure] });   // 63.7 (b): whose building it is, at person scale
}

export function getHazardWorldPosition(
  hazard: HazardState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[hazard.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, hazard.surfaceDir);
  const normal = vecNormalize(hazard.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getStructureWorldPosition(
  structure: StructureState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[structure.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, structure.surfaceDir) + (structure.baseLift ?? 0);   // 67.52: a build on its spot's fine ground
  const normal = vecNormalize(structure.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getRuinWorldPosition(
  ruin: RuinState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[ruin.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, ruin.surfaceDir);
  const normal = vecNormalize(ruin.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getFloraWorldPosition(
  flora: FloraState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[flora.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, flora.surfaceDir);
  const normal = vecNormalize(flora.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getRockWorldPosition(
  rock: RockState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[rock.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, rock.surfaceDir);
  const normal = vecNormalize(rock.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getEffectWorldPosition(
  effect: EnvironmentalEffectState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[effect.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, effect.surfaceDir);
  const normal = vecNormalize(effect.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getCarryableWorldPosition(
  carryable: CarryableObjectState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[carryable.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, carryable.surfaceDir);
  const normal = vecNormalize(carryable.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getGroundItemWorldPosition(
  item: GroundItemState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[item.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, item.surfaceDir);
  const normal = vecNormalize(item.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getCollectibleWorldPosition(
  collectible: CollectibleNode,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[collectible.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, collectible.surfaceDir);
  const normal = vecNormalize(collectible.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

export function getDrillSiteWorldPosition(
  drill: DrillSiteState,
  planets: PlanetState[]
): Vec3 {
  const planet = planets[drill.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, drill.surfaceDir);
  const normal = vecNormalize(drill.surfaceDir);
  return vecAdd(planet.position, vecScale(normal, surfaceRadius));
}

// =============================================================================
// FLAG PLANTING
// =============================================================================

export function canPlantFlag(player: PlayerState): boolean {
  return (
    player.alive &&
    !player.airborne &&
    player.flagsRemaining > 0 &&
    player.movementRemaining >= PLANT_FLAG_MOVEMENT_COST
  );
}

export function plantFlag(
  state: GameState,
  playerId: number,
  surfaceDir: Vec3
): GameState {
  const playerIndex = state.players.findIndex(p => p.id === playerId);
  if (playerIndex < 0) return state;

  const player = state.players[playerIndex];
  if (!canPlantFlag(player)) return state;

  // Mode enforcement: check if planting is allowed on this planet
  if (!canPlantOnPlanet(player, player.planetIndex, state.plantedFlags, state.config.modeConfig)) {
    return state;
  }

  const flag: PlantedFlagState = {
    id: state.plantedFlags.length,
    ownerId: playerId,
    teamIndex: player.teamIndex,
    planetIndex: player.planetIndex,
    surfaceDir: vecNormalize(surfaceDir),
    health: state.config.modeConfig.stakeHP ?? undefined,
    alive: true,
  };

  const newPlayers = state.players.map((p, i) =>
    i === playerIndex
      ? {
          ...p,
          flagsRemaining: p.flagsRemaining - 1,
          movementRemaining: p.movementRemaining - PLANT_FLAG_MOVEMENT_COST,
        }
      : p
  );

  return {
    ...state,
    players: newPlayers,
    plantedFlags: [...state.plantedFlags, flag],
  };
}

// =============================================================================
// TURN INPUT RECORDING
// =============================================================================

